import { z } from "zod";

import { MetaDataSchema } from "@/schemas/meta-data";

import { CompanySchema } from "../../Company/schema/company";
import { DecisionConfirmationTypes } from "../../Course/enums/DecisionConfirmationTypes.enum";
import { CourseStatus } from "../enums/CourseStatus.enum";
import { FileAssignment } from "../enums/FileAssignment.enum";
import { FileTypes } from "../enums/FileTypes.enum";
import { PhaseEffect } from "../enums/PhaseEffect.enum";
import { PhaseEndResult } from "../enums/PhaseEndResult.enum";

export const CourseFileSchema = z.object({
	id: z.number(),
	fullFilePath: z.string(),
	fileName: z.string(),
	fileType: z.nativeEnum(FileTypes),
	fileAssignment: z.nativeEnum(FileAssignment),
	fileLength: z.number(),
	hash: z.string(),
	/**
	 * Whether an AI model produced this file — the EU AI Act transparency marker.
	 *
	 * Optional because it is only sent by the endpoints that have been extended to carry
	 * it, and because a file uploaded before the column existed answers `false` rather than
	 * nothing. Never inferred client-side: what a file is made of is the server's to say.
	 */
	aiGenerated: z.boolean().nullable().optional(),
	aiProvenance: z
		.object({
			source: z.string(),
			modelDisplayName: z.string().nullable().optional(),
			provider: z.string().nullable().optional(),
			generatedAt: z.string().nullable().optional(),
		})
		.nullable()
		.optional(),
});

export const TtsVoiceSchema = z.object({
	voiceId: z.string(),
	name: z.string(),
	language: z.string(),
	previewUrl: z.string().optional(),
	accent: z.string().optional(),
	gender: z.string().optional(),
	age: z.string().optional(),
	useCase: z.string().optional(),
	category: z.string().optional(),
	verifiedLanguages: z.array(z.string()).optional(),
});

export type TtsVoice = z.infer<typeof TtsVoiceSchema>;

export const CourseTagSchema = z.object({
	id: z.number().optional(),
	name: z.string().optional(),
});

export const CoursesSchema = z.object({
	id: z.number(),
	name: z.string(),
	description: z.string().nullable().optional(),
	forWhom: z.string().nullable().optional(),
	version: z.number().optional(),
	json: z
		.string()
		.transform((arg) => JSON.parse(arg))
		.optional(),
	company: CompanySchema,
	courseFiles: CourseFileSchema.array(),
	language: z.enum(["EN", "DE"]),
	tag: CourseTagSchema.optional().nullable(),
	status: z.nativeEnum(CourseStatus),
	author: z.string().optional().nullable(),
	/**
	 * Which saved version the live content was last published from — BCM360-139. The
	 * content itself still lives on the scenario, so nothing that plays it cares about
	 * versioning; this only says where it came from, and marks that version as the active
	 * one in the list.
	 */
	publishedVersionId: z.number().nullable().optional(),
});

/**
 * A saved snapshot of a scenario — BCM360-139.
 *
 * `publishedAt` is stamped every time this version was made the live one, so it answers
 * "has this ever been published"; whether it is the one playing *now* is a separate
 * question, answered by the scenario's own `publishedVersionId`. A version can have been
 * published once and since been replaced.
 */
export const CourseVersionSchema = z.object({
	id: z.number(),
	courseId: z.number(),
	label: z.string().nullable().optional(),
	/**
	 * The rolling working copy — at most one per scenario. Every save rewrites it in place
	 * rather than adding to the history, and nothing in it is live until it is published.
	 */
	isDraft: z.boolean().optional(),
	/**
	 * The draft's save counter (BCM360-383). The editor sends the revision it loaded with each
	 * save, and the API refuses the save (409 STALE_DRAFT) when the draft moved on meanwhile —
	 * someone else, or an AI client over MCP, saved it.
	 */
	revision: z.number().optional(),
	name: z.string().nullable().optional(),
	description: z.string().nullable().optional(),
	forWhom: z.string().nullable().optional(),
	json: z.unknown().optional(),
	aiParams: z.unknown().optional(),
	language: z.enum(["EN", "DE"]).optional(),
	createdByUserId: z.number().nullable().optional(),
	createdByName: z.string().nullable().optional(),
	publishedAt: z.string().nullable().optional(),
	createdAt: z.string(),
	updatedAt: z.string().optional(),
});

export type CourseVersion = z.infer<typeof CourseVersionSchema>;

export const CreateCoursesSchema = z.object({
	name: z.string(),
	author: z.string().optional(),
	description: z.string().optional(),
	forWhom: z.string().optional(),
	version: z.number().optional(),
	json: z.any().optional(),
	tag: CourseTagSchema.optional().nullable(),
	language: z.enum(["EN", "DE"]),
	status: z.nativeEnum(CourseStatus),
});

export const GenerateCourseSchema = z.object({
	industry: z.string().min(1, "Industry required"),
	companySize: z.string().min(1, "Company size required"),
	primaryIncidentType: z.string().min(1, "PrimaryIncidentType required"),
	specificCompanyDeficits: z.array(z.string()).optional(),
	specificNotes: z.string().optional(),
	language: z.enum(["English", "German"]).default("German"),
});

export const PhaseDataSchema = z
	.object({
		id: z.number().nullable(),
		phaseName: z.string(),
		timeLimit: z.number().optional(),
		phaseEndText: z.string().optional(),
		phaseEndResult: z.nativeEnum(PhaseEndResult).optional(),
		confirmationRequired: z.nativeEnum(DecisionConfirmationTypes).optional(),
		timeLeftDecisionId: z.number().min(0).optional().nullable(),
		content: z.string().min(1, "Content is required"),
		contentType: z.nativeEnum(FileTypes),
		decisionName: z.string().optional(),
		autoplay: z.boolean().optional().default(false),
		large: z.boolean().optional().default(false),
		spoilerContent: z
			.object({
				content: z.string(),
				title: z.string().optional(),
			})
			.optional(),
		decisionOptions: z
			.array(
				z.object({
					option: z.string(),
					phaseId: z.number(),
				})
			)
			.min(1, { message: "The array must have at least one element" })
			.optional(),
		timeDelayedContent: z
			.array(
				z
					.object({
						content: z.string(),
						startTimeInSeconds: z.number(),
						endTimeInSeconds: z.number().optional(),
						contentType: z.nativeEnum(FileTypes),
						title: z.string(),
						autoplay: z.boolean().optional().default(false),
					})
					.refine(
						(data) =>
							!data.endTimeInSeconds || data.endTimeInSeconds > data.startTimeInSeconds + 10,
						{
							message:
								"endTimeInSeconds must be at least 10 seconds greater than startTimeInSeconds",
							path: ["endTimeInSeconds"],
						}
					)
			)
			.optional(),
		position: z.object({ x: z.number(), y: z.number() }).optional(),
		effects: z.array(z.nativeEnum(PhaseEffect)).optional(),
	})
	.superRefine(
		(
			{ decisionName, decisionOptions, timeLeftDecisionId, timeLimit, confirmationRequired },
			ctx
		) => {
			if (decisionName && (!decisionOptions || decisionOptions.length === 0)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: "Decision name must be set if decision options are empty",
					path: ["decisionOptions"],
				});
			}

			if ((!decisionName || decisionName === "") && decisionOptions && decisionOptions.length > 0) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: "Decision name must be set",
					path: ["decisionName"],
				});
			}

			if (decisionOptions && decisionOptions.length > 1) {
				decisionOptions.forEach((option) => {
					if (!option.option) {
						ctx.addIssue({
							code: z.ZodIssueCode.custom,
							message: "Decision option must be set",
							path: ["decisionOptions", "option"],
						});
					}
				});
			}

			if (timeLeftDecisionId && !timeLimit) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: "Time limit must be set if time left decision id is set",
					path: ["timeLimit"],
				});
			}

			// TODO(F7, BCM360-139 follow-up): this reports on `timeLimit`'s sibling but reads
			// as though the time limit were missing — the field actually outlined in the Edit
			// decision modal is "automatically selected option when the time limit is
			// reached", and the limit itself was already set. Rename the message to the field
			// it means. Open question to settle at the same time: several phases in a live,
			// published scenario carry this warning, so decide whether it blocks publishing
			// or stays advisory — today publishing does not consult it at all.
			if (timeLimit && !timeLeftDecisionId) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: "Time left decision id must be set if time limit is set",
					path: ["timeLeftDecisionId"],
				});
			}

			if (
				confirmationRequired === DecisionConfirmationTypes.NONE &&
				decisionOptions &&
				decisionOptions.length > 1
			) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: "Confirmation required must be set if more than one decision option is set",
					path: ["confirmationRequired"],
				});
			}
		}
	);

export const JsonSchema = z.object({
	scenarioName: z.string(),
	author: z.string().optional().default(""),
	Content: z.array(PhaseDataSchema),
	timeDelayedContent: z
		.array(
			z
				.object({
					content: z.string(),
					startTimeInSeconds: z.number(),
					endTimeInSeconds: z.number().optional(),
					contentType: z.nativeEnum(FileTypes),
					title: z.string(),
					autoplay: z.boolean().optional().default(false),
				})
				.refine(
					(data) => !data.endTimeInSeconds || data.endTimeInSeconds > data.startTimeInSeconds + 10,
					{
						message: "endTimeInSeconds must be at least 10 seconds greater than startTimeInSeconds",
						path: ["endTimeInSeconds"],
					}
				)
		)
		.optional(),
});

export const UpdateCoursesSchema = z.object({
	id: z.number().optional(),
	name: z.string().min(1, "Name is required").optional(),
	description: z.string().min(1, "Description is required").nullable().optional(),
	forWhom: z.string().nullable().optional(),
	version: z.number().optional(),
	language: z.enum(["EN", "DE"]).optional(),
	json: z.any().optional(),
	tag: CourseTagSchema.optional().nullable(),
	status: z.nativeEnum(CourseStatus).optional(),
	author: z.string().optional().nullable(),
});

export const UpdateCoursesSchema2 = z.object({
	id: z.number().optional(),
	name: z.string().min(1, "Name is required").optional(),
	description: z.string().min(1, "Description is required").optional(),
	forWhom: z.string().optional(),
	version: z.number().optional(),
	language: z.enum(["EN", "DE"]).optional(),
	json: JsonSchema.optional(),
	tag: CourseTagSchema.optional(),
	status: z.nativeEnum(CourseStatus).optional(),
});

export const CoursesResponseSchema = z.object({
	data: z.array(CoursesSchema),
	meta: MetaDataSchema,
});

export const GeneratedCourseSchema = z.object({
	message: z.string(),
});

export type Courses = z.infer<typeof CoursesSchema>;

export type CourseTag = z.infer<typeof CourseTagSchema>;

export type CourseFile = z.infer<typeof CourseFileSchema>;

export type CreateCourses = z.infer<typeof CreateCoursesSchema>;

export type UpdateCourses = z.infer<typeof UpdateCoursesSchema>;

export type CoursesResponse = z.infer<typeof CoursesResponseSchema>;

export type GenerateCourse = z.infer<typeof GenerateCourseSchema>;

export type GeneratedCourse = z.infer<typeof GeneratedCourseSchema>;
